<template>
  <AuthLayout
    :sidebar-title="magicLinkExpired ? `Link expired` : `Check your inbox`"
    :sidebar-text="sidebarText"
    :support-phone="supportPhone"
  >
    <div class="auth-login__success-icon">
      <AuthIcon name="check" />
    </div>

    <h2 class="auth-login__card-title auth-login__card-title--center" tabindex="-1">
      <template v-if="! magicLinkExpired">
        Magic link sent
      </template>
      <template v-else>
        Magic link expired
      </template>
    </h2>

    <p class="auth-login__card-subtitle" style="text-align: center; margin-bottom: 0">
      <template v-if=" !magicLinkExpired">
        We sent a secure sign-in link to
        <span class="auth-login__masked">{{ maskedIdentifier }}</span>.
        <template v-if="identifierType === 'email'">
          Click the link to continue signing in. <template v-if="! isTrustedDevice">Next, you'll be asked to secure your account with a code sent to your mobile phone.</template>
        </template>
        <template v-else>
          Click the link to continue signing in. <template v-if="! isTrustedDevice">Next, you'll be asked to secure your account with a code sent to your email address.</template>
        </template>
      </template>
      <template v-else>
        The link sign-in link you’ve clicked has expired. Click resend link below to receive a new link to continue signing in.
      </template>
    </p>

    <div v-if="feedback" class="auth-login__success-banner" role="status">
      <AuthIcon name="check" />
      <span>{{ feedback }}</span>
    </div>

    <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>

    <div class="auth-login__actions">
      <AuthButton
        icon="refresh"
        :loading="loading"
        :disabled="isCoolingDown"
        @click="emit('resend')"
      >
        Resend the link
      </AuthButton>
    </div>

    <p v-if="isCoolingDown" class="auth-login__cooldown-hint">
      Please wait {{ countdown }} before requesting another link.
    </p>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('back')">
      ← Back to login options
    </button>
  </AuthLayout>
</template>

<script setup>
import { computed } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthIcon from '../../shared/components/AuthIcon.vue'
import { maskIdentifier } from '../utils/maskIdentifier.js'

const props = defineProps({
  channel:          { type: String, required: true },
  identifier:       { type: String, required: true },
  identifierType:   { type: String, required: true },
  loading:          { type: Boolean, default: false },
  error:            { type: String,  default: '' },
  feedback:         { type: String,  default: '' },
  isCoolingDown:    { type: Boolean, default: false },
  countdown:        { type: String,  default: '1:00' },
  supportPhone:     { type: String,  default: '815-908-2020' },
  expiresInMinutes: { type: Number, default: 60 },
  magicLinkExpired: { type: Boolean, default: false },
  isTrustedDevice:    { type: Boolean, default: false },
})

const emit = defineEmits(['resend', 'back'])

const maskedIdentifier = computed(() => maskIdentifier(props.identifier))
const sidebarText = computed(() => {
  const expiresInHours = props.expiresInMinutes / 60;
  const expiresInDays = expiresInHours / 24;

  let unit = props.expiresInMinutes <= 1 ? 'minute' : 'minutes'
  let expires = props.expiresInMinutes;

  if (expiresInDays >= 1) {
    unit = expiresInDays === 1 ? 'day' : 'days'
    expires = expiresInDays;
  } else if (expiresInHours >= 1) {
    unit = expiresInHours === 1 ? 'hour' : 'hours'
    expires = expiresInHours;
  }

  if (props.magicLinkExpired) {
    return `The link sign-in link you’ve clicked has expired. Click resend link below to receive a new link to continue signing in.`;
  }

  return `The link is valid for ${parseInt(expires)} ${unit}. Don't see it? Check your spam folder or click Resend.`
})
</script>
